import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isoWeek } from '@pz/core';
import { h, hh, money, month, pct } from '../lib/format';
import { useQ } from '../lib/hooks';
import { HBars, WeekBars } from './charts';
import { MoneyLockNotice } from './step-up';
import { ElementHtmlButton } from './report-host';
import { ErrorBox, Loading, Panel, Pill, Stat, td, th } from './ui';

/** Key figures of one project (project page, tab "Kennzahlen"). */
export function ProjectKpiPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const q = useQ('kpi.project', { projectId });
  const k = q.data?.kpi;
  const box = useRef<HTMLDivElement>(null);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data || !k) return <ErrorBox error={q.error} />;
  const plan = q.data.plan;
  const billing = q.data.billing;
  return (
    <div ref={box} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">
          {q.data.project.number} {q.data.project.name}
        </h2>
        {/* the key figures as an interactive file, e.g. for the client (round 5, request 16) */}
        <ElementHtmlButton target={box} title={`${q.data.project.number} ${q.data.project.name}: ${t('projects.tabs.kpi')}`} fileName={`Kennzahlen_${q.data.project.number}_${q.data.to}.html`} />
      </div>
      <MoneyLockNotice locked={q.data.moneyLocked} />
      <div className="grid grid-cols-2 gap-4 rounded-lg border border-line bg-sheet p-4 md:grid-cols-3 xl:grid-cols-6">
        <Stat label={t('common.booked')} value={hh(k.totalMinutes, 1)} />
        <Stat
          label={t('kpi.budgetUsed')}
          value={k.budgetMinutes ? pct(k.budgetUsed) : '-'}
          hint={k.budgetMinutes ? `${t('common.budget')} ${hh(k.budgetMinutes, 0)}` : undefined}
          tone={k.budgetMinutes ? (k.budgetUsed > 1 ? 'bad' : k.budgetUsed > 0.8 ? 'warn' : 'good') : undefined}
        />
        <Stat label={t('kpi.billable')} value={hh(k.billableMinutes, 1)} hint={`${t('billingType.included')}: ${hh(k.includedMinutes, 1)}`} />
        <Stat label={t('kpi.plannedRemaining')} value={hh(q.data.plannedRemainingMinutes, 0)} />
        {k.salesValueCents !== null && <Stat label={t('kpi.salesValue')} value={money(k.salesValueCents)} hint={`${t('kpi.unbilledValue')}: ${money(k.unbilledValueCents)}`} />}
        {k.costCents !== null && <Stat label={t('kpi.cost')} value={money(k.costCents)} hint={k.costMissingMinutes ? t('kpi.costMissing', { hours: h(k.costMissingMinutes, 1) }) : undefined} />}
      </div>
      <div className="grid grid-cols-2 gap-4 rounded-lg border border-line bg-sheet p-4 md:grid-cols-4">
        <Stat label={t('projectKpi.tasksWithPlan')} value={`${plan.tasksWithPlan} / ${plan.tasksTotal}`} hint={t('projectKpi.planHours', { plan: h(plan.plannedMinutes, 0), booked: h(plan.bookedOnPlannedMinutes, 0) })} />
        <Stat
          label={t('projectKpi.done')}
          value={plan.doneBookedMinutes ? pct(plan.donePlannedMinutes / plan.doneBookedMinutes) : '-'}
          hint={plan.doneTasks ? t('projectKpi.doneHint', { count: plan.doneTasks, plan: h(plan.donePlannedMinutes, 0), booked: h(plan.doneBookedMinutes, 0) }) : t('kpi.efficiencyNone')}
        />
        <Stat label={t('projectKpi.overPlan')} value={plan.overPlanCount} tone={plan.overPlanCount ? 'warn' : undefined} />
        {billing && (
          <Stat
            label={t('projectKpi.invoicedFactor')}
            value={billing.recordedMinutes ? pct(billing.factor) : '-'}
            hint={billing.recordedMinutes ? t('projectKpi.invoicedHint', { invoiced: h(billing.invoicedMinutes, 1), recorded: h(billing.recordedMinutes, 1) }) : t('projectKpi.noInvoiced')}
          />
        )}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t('kpi.weekly')}>
          <WeekBars
            showCapacity={false}
            data={q.data.weeks.map((w) => {
              const n = isoWeek(w.weekStart).week;
              return { label: `${t('kpi.weekPrefix')} ${n}`, axis: `${n}`, booked: w.bookedMinutes, billable: w.billableMinutes, capacity: 0 };
            })}
          />
        </Panel>
        <Panel title={t('kpi.byPerson')}>
          <HBars head={t('common.person')} rows={q.data.byPerson.map((p) => ({ label: p.name, value: p.minutes, billable: p.billable }))} />
        </Panel>
        <Panel title={t('kpi.byMonth')}>
          <HBars head={t('common.month')} rows={k.byMonth.map((m) => ({ label: month(`${m.month}-01`), value: m.minutes, billable: m.billable }))} />
        </Panel>
        <Panel title={t('projectKpi.overPlanList')} bodyClassName="p-0">
          {plan.overPlan.length === 0 ? (
            <p className="p-4 text-sm text-graphite">{t('projectKpi.noOverPlan')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-sheet-2">
                <tr>
                  <th className={th}>{t('common.task')}</th>
                  <th className={`${th} text-right`}>{t('projectKpi.plan')}</th>
                  <th className={`${th} text-right`}>{t('common.booked')}</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody>
                {plan.overPlan.map((x) => (
                  <tr key={x.id} className="border-b border-line/60 last:border-0">
                    <td className={td}>
                      <span className="num font-semibold text-accent">{x.ref}</span> {x.title}
                    </td>
                    <td className={`${td} num text-right`}>{h(x.plannedMinutes, 1)}</td>
                    <td className={`${td} num text-right`}>{h(x.bookedMinutes, 1)}</td>
                    <td className={td}>
                      <Pill tone="warn">{pct(x.bookedMinutes / x.plannedMinutes)}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>
    </div>
  );
}
